import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Copy,
  ExternalLink,
  Info,
  XCircle,
  Clock,
  Calendar,
  User,
  FileText,
  ArrowUpRight,
  ListFilter,
  Inbox,
} from 'lucide-react';

// Outcome colors for the unified intake events
const OUTCOME_COLORS = {
  success_created: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  success_updated: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  success_updated_dedupe: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  success: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",  // Generic success = Accepted (green)
  rejected_validation: "bg-red-500/20 text-red-300 border-red-500/30",
  rejected_duplicate: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  rejected_no_lead_match: "bg-orange-500/20 text-orange-300 border-orange-500/30",
  error_exception: "bg-red-500/20 text-red-300 border-red-500/30",
  // Legacy statuses
  accepted: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  accepted_duplicate: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  duplicate: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  rejected: "bg-red-500/20 text-red-300 border-red-500/30",
};

const OUTCOME_ICONS = {
  success_created: CheckCircle,
  success_updated: CheckCircle,
  success_updated_dedupe: Copy,
  success: CheckCircle,  // Generic success
  rejected_validation: XCircle,
  rejected_duplicate: AlertCircle,
  rejected_no_lead_match: AlertCircle,
  error_exception: XCircle,
  // Legacy
  accepted: CheckCircle,
  accepted_duplicate: Copy,
  duplicate: AlertCircle,
  rejected: XCircle,
};

const EVENT_TYPE_COLORS = {
  lead_intake: "bg-indigo-500/20 text-indigo-300 border-indigo-500/30",
  appointment_webhook: "bg-purple-500/20 text-purple-300 border-purple-500/30",
};

const EVENT_TYPE_ICONS = {
  lead_intake: User,
  appointment_webhook: Calendar,
};

/**
 * Unified Intake Events Viewer
 * Admin UI for viewing both lead intake and appointment webhook events
 */
const IntakeEventsViewer = ({ className = "", tenantId = null }) => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [outcomeSummary, setOutcomeSummary] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  
  // Inbound health summary from audit errors
  const [healthData, setHealthData] = useState({
    lead_intake_error: null,
    ghl_appointment_error: null,
  });
  const [healthLoading, setHealthLoading] = useState(true);
  
  // Filters
  const [eventTypeFilter, setEventTypeFilter] = useState("all");
  const [outcomeFilter, setOutcomeFilter] = useState("all");
  const [traceIdFilter, setTraceIdFilter] = useState("");  // P1: Journey correlation filter
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  
  // Filter visibility state
  const [showFilters, setShowFilters] = useState(false);
  
  // Quick filter state (for pills)
  const [activeQuickFilter, setActiveQuickFilter] = useState("all");
  
  // Detail dialog
  const [detailDialog, setDetailDialog] = useState({ open: false, event: null, loading: false });

  // P0 TENANT ISOLATION: Build params with tenant_id for SuperAdmin cross-tenant access
  const buildTenantParams = useCallback(() => {
    return tenantId ? { tenant_id: tenantId } : {};
  }, [tenantId]);

  // Load sources for filter dropdown
  const loadSources = useCallback(async () => {
    try {
      const res = await api.get("/lead-sources", { params: buildTenantParams() });
      setSources(res.data?.sources || []);
    } catch (err) {
      console.error("Failed to load sources:", err);
    }
  }, [buildTenantParams]);
  
  // Load health summary from audit errors endpoint
  const loadHealthData = useCallback(async () => {
    setHealthLoading(true);
    try {
      const res = await api.get("/admin/audit/inbound-errors", { params: buildTenantParams() });
      setHealthData(res.data || { lead_intake_error: null, ghl_appointment_error: null });
    } catch (err) {
      // Non-critical - just log
      console.warn("Could not load inbound health data:", err);
    } finally {
      setHealthLoading(false);
    }
  }, [buildTenantParams]);

  // Load events from unified webhook-intake/events endpoint
  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      // P0 TENANT ISOLATION: Use set() for tenant_id to prevent duplicates
      if (tenantId) params.set("tenant_id", tenantId);
      if (eventTypeFilter && eventTypeFilter !== "all") params.append("event_type", eventTypeFilter);
      if (outcomeFilter && outcomeFilter !== "all") params.append("outcome", outcomeFilter);
      if (traceIdFilter && traceIdFilter.trim()) params.append("trace_id", traceIdFilter.trim());  // P1: Journey correlation
      if (startDate) params.append("start_date", startDate);
      if (endDate) params.append("end_date", endDate);
      params.append("limit", "100");
      
      const res = await api.get(`/webhook-intake/events?${params.toString()}`);
      setEvents(res.data?.events || []);
      setOutcomeSummary(res.data?.outcome_summary || {});
      setTotalCount(res.data?.total || 0);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load intake events");
      toast({
        title: "Error loading events",
        description: err?.response?.data?.detail || "Please try again",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [tenantId, eventTypeFilter, outcomeFilter, traceIdFilter, startDate, endDate]);

  useEffect(() => {
    loadSources();
    loadHealthData();
  }, [loadSources, loadHealthData]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);
  
  // Quick filter handler
  const handleQuickFilter = (filterType) => {
    setActiveQuickFilter(filterType);
    switch (filterType) {
      case "all":
        setEventTypeFilter("all");
        setOutcomeFilter("all");
        break;
      case "lead_intake":
        setEventTypeFilter("lead_intake");
        setOutcomeFilter("all");
        break;
      case "appointment_webhook":
        setEventTypeFilter("appointment_webhook");
        setOutcomeFilter("all");
        break;
      case "errors":
        setEventTypeFilter("all");
        setOutcomeFilter("error_exception");
        break;
      default:
        break;
    }
  };
  
  // Format relative timestamp for health display
  const formatRelativeTime = (isoStr) => {
    if (!isoStr) return "Never";
    const date = new Date(isoStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // View event detail
  const viewEventDetail = async (event) => {
    setDetailDialog({ open: true, event, loading: false });
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Get outcome display text
  const getOutcomeDisplay = (outcome) => {
    if (!outcome) return "—";
    
    // Handle prefixed outcomes from unified summary (e.g., "lead:accepted", "appointment:success_created")
    if (outcome.includes(":")) {
      const [eventType, rawOutcome] = outcome.split(":");
      const eventLabel = eventType === "lead" ? "Lead" : "Appt";
      const outcomeLabel = getOutcomeLabel(rawOutcome);
      return `${eventLabel} – ${outcomeLabel}`;
    }
    
    // Handle raw outcome values (for table display)
    return getOutcomeLabel(outcome);
  };
  
  // Get human-readable label for raw outcome value
  const getOutcomeLabel = (outcome) => {
    const outcomeMap = {
      // Unified terminology - "Accepted" for successful intake
      success_created: "Accepted",
      success_updated: "Accepted (Update)",
      success_updated_dedupe: "Accepted (Dedupe)",
      success: "Accepted",  // Generic success maps to "Accepted"
      accepted: "Accepted",
      accepted_duplicate: "Accepted (Dup)",
      // Rejection outcomes
      rejected_validation: "Rejected",
      rejected_duplicate: "Duplicate",
      rejected_no_lead_match: "No Lead Match",
      duplicate: "Duplicate",
      rejected: "Rejected",
      // Errors
      error_exception: "Error",
      error: "Error",
      failed: "Failed",
    };
    return outcomeMap[outcome] || outcome;
  };

  // Get event type display
  const getEventTypeDisplay = (eventType) => {
    if (!eventType) return "—";
    const typeMap = {
      lead_intake: "Lead",
      appointment_webhook: "Appointment",
    };
    return typeMap[eventType] || eventType;
  };

  // Truncate ID for display
  const truncateId = (id) => {
    if (!id) return "—";
    return id.length > 12 ? `${id.slice(0, 8)}...` : id;
  };

  return (
    <Card className={`bg-slate-900/50 border-slate-800 ${className}`} data-testid="intake-events-viewer">
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-xl text-slate-100 flex items-center gap-2" data-testid="intake-events-title">
              <Inbox className="w-5 h-5 text-indigo-400" />
              Intake Events
            </CardTitle>
            <CardDescription className="text-slate-400 mt-1">
              View inbound integration events and webhook processing results
            </CardDescription>
          </div>
          <div className="flex items-center gap-3">
            {/* Health Status Badges - Stacked vertically */}
            <div className="flex flex-col gap-1.5 text-right">
              <div className="flex items-center gap-2 justify-end">
                <span className="text-sm text-slate-400">Appointments</span>
                <Badge className={healthData.ghl_appointment_error 
                  ? "bg-red-500/20 text-red-400" 
                  : "bg-emerald-500/20 text-emerald-400"
                }>
                  {healthData.ghl_appointment_error ? (
                    <AlertCircle className="w-3 h-3 mr-1" />
                  ) : (
                    <CheckCircle className="w-3 h-3 mr-1" />
                  )}
                  {healthData.ghl_appointment_error ? "Error" : "Healthy"}
                </Badge>
              </div>
              <div className="flex items-center gap-2 justify-end">
                <span className="text-sm text-slate-400">Leads</span>
                <Badge className={healthData.lead_intake_error 
                  ? "bg-red-500/20 text-red-400" 
                  : "bg-emerald-500/20 text-emerald-400"
                }>
                  {healthData.lead_intake_error ? (
                    <AlertCircle className="w-3 h-3 mr-1" />
                  ) : (
                    <CheckCircle className="w-3 h-3 mr-1" />
                  )}
                  {healthData.lead_intake_error ? "Error" : "Healthy"}
                </Badge>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { loadEvents(); loadHealthData(); }}
              className="text-slate-400 hover:text-slate-200"
              data-testid="intake-events-refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Filter Toggle Row - Aligned with Outbound Events */}
        <div className="flex items-center gap-2 mb-4" data-testid="event-type-filter">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="border-slate-700 text-slate-300"
            data-testid="intake-events-filter-toggle"
          >
            <ListFilter className="w-4 h-4 mr-1" />
            Filters
          </Button>
          {(eventTypeFilter !== "all" || outcomeFilter !== "all" || startDate || endDate || traceIdFilter) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setEventTypeFilter("all");
                setOutcomeFilter("all");
                setStartDate("");
                setEndDate("");
                setTraceIdFilter("");
                setActiveQuickFilter("all");
              }}
              className="text-slate-400"
              data-testid="intake-events-clear-filters"
            >
              Clear
            </Button>
          )}
          {/* Active Trace ID Filter Badge */}
          {traceIdFilter && (
            <Badge
              variant="outline"
              className="bg-emerald-500/10 text-emerald-300 border-emerald-500/30 gap-1"
            >
              <ExternalLink className="w-3 h-3" />
              Trace: {traceIdFilter.length > 12 ? `${traceIdFilter.substring(0, 12)}...` : traceIdFilter}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setTraceIdFilter("")}
                className="h-4 w-4 p-0 ml-1 text-emerald-400 hover:text-emerald-200"
                data-testid="intake-events-trace-badge-clear"
              >
                <XCircle className="w-3 h-3" />
              </Button>
            </Badge>
          )}
        </div>

        {/* Collapsible Filters Panel */}
        {showFilters && (
          <div className="flex flex-wrap items-end gap-3 p-3 bg-slate-800/50 rounded-lg mb-4" data-testid="intake-events-filters">
            <div className="space-y-1 w-36">
              <Label className="text-xs text-slate-400">Event Type</Label>
              <Select value={eventTypeFilter} onValueChange={(val) => {
                setEventTypeFilter(val);
                setActiveQuickFilter(val === "all" ? "all" : val);
              }}>
                <SelectTrigger className="h-8 bg-slate-900 border-slate-700 text-slate-200">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="all" className="text-slate-100">All Types</SelectItem>
                  <SelectItem value="lead_intake" className="text-slate-100">Lead Intake</SelectItem>
                  <SelectItem value="appointment_webhook" className="text-slate-100">Appointment</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-1 w-36">
              <Label className="text-xs text-slate-400">Outcome</Label>
              <Select value={outcomeFilter} onValueChange={setOutcomeFilter}>
                <SelectTrigger className="h-8 bg-slate-900 border-slate-700 text-slate-200">
                  <SelectValue placeholder="All Outcomes" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="all" className="text-slate-100">All Outcomes</SelectItem>
                  <SelectItem value="success_created" className="text-slate-100">Accepted</SelectItem>
                  <SelectItem value="accepted" className="text-slate-100">Accepted (Lead)</SelectItem>
                  <SelectItem value="success_updated" className="text-slate-100">Accepted (Update)</SelectItem>
                  <SelectItem value="rejected_validation" className="text-slate-100">Rejected</SelectItem>
                  <SelectItem value="rejected_no_lead_match" className="text-slate-100">No Lead Match</SelectItem>
                  <SelectItem value="error_exception" className="text-slate-100">Error</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-1 w-36">
              <Label className="text-xs text-slate-400">Start Date</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="h-8 bg-slate-900 border-slate-700 text-slate-200 [&::-webkit-calendar-picker-indicator]:opacity-50 [&::-webkit-calendar-picker-indicator]:hover:opacity-100"
                data-testid="intake-events-start-date"
              />
            </div>
            
            <div className="space-y-1 w-36">
              <Label className="text-xs text-slate-400">End Date</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="h-8 bg-slate-900 border-slate-700 text-slate-200 [&::-webkit-calendar-picker-indicator]:opacity-50 [&::-webkit-calendar-picker-indicator]:hover:opacity-100"
                data-testid="intake-events-end-date"
              />
            </div>
            
            <div className="space-y-1 w-44">
              <Label className="text-xs text-slate-400">Trace ID</Label>
              <div className="flex gap-1">
                <Input
                  type="text"
                  value={traceIdFilter}
                  onChange={(e) => setTraceIdFilter(e.target.value)}
                  placeholder="Filter by trace..."
                  className="h-8 bg-slate-900 border-slate-700 text-slate-200 font-mono text-xs"
                  data-testid="intake-events-trace-id-filter"
                />
                {traceIdFilter && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setTraceIdFilter("")}
                    className="h-8 px-2 text-slate-400 hover:text-slate-200 hover:bg-slate-800"
                    data-testid="intake-events-trace-id-clear"
                  >
                    <XCircle className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Events Table */}
        <div className="overflow-hidden rounded-lg ring-1 ring-white/10">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-slate-400 text-xs">Received</TableHead>
                <TableHead className="text-slate-400 text-xs">Type</TableHead>
                <TableHead className="text-slate-400 text-xs">Outcome</TableHead>
                <TableHead className="text-slate-400 text-xs">MissionAssist ID</TableHead>
                <TableHead className="text-slate-400 text-xs">GHL Contact</TableHead>
                <TableHead className="text-slate-400 text-xs">Appt ID / Calendar</TableHead>
                <TableHead className="text-slate-400 text-xs w-20">Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow className="border-white/10">
                  <TableCell colSpan={7} className="py-8">
                    <div className="flex flex-col items-center gap-2">
                      <Skeleton className="h-4 w-48 bg-white/10" />
                      <Skeleton className="h-4 w-32 bg-white/10" />
                    </div>
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow className="border-white/10">
                  <TableCell colSpan={7} className="py-8 text-center text-red-400">
                    <AlertCircle className="h-5 w-5 mx-auto mb-2" />
                    {error}
                  </TableCell>
                </TableRow>
              ) : events.length === 0 ? (
                <TableRow className="border-white/10">
                  <TableCell colSpan={7} className="py-8 text-center text-slate-400">
                    No intake events found
                  </TableCell>
                </TableRow>
              ) : (
                events.map((event) => {
                  const OutcomeIcon = OUTCOME_ICONS[event.outcome] || OUTCOME_ICONS[event.status] || Info;
                  const EventTypeIcon = EVENT_TYPE_ICONS[event.event_type] || FileText;
                  const outcomeKey = event.outcome || event.status;
                  
                  return (
                    <TableRow 
                      key={event.id} 
                      className="border-white/10"
                      data-testid={`intake-event-row-${event.id}`}
                    >
                      <TableCell className="text-slate-300 text-sm">
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3 text-slate-500" />
                          {formatDate(event.received_at)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          className={`${EVENT_TYPE_COLORS[event.event_type] || "bg-slate-500/20 text-slate-300"} border`}
                          data-testid={`intake-event-type-${event.id}`}
                        >
                          <EventTypeIcon className="h-3 w-3 mr-1" />
                          {getEventTypeDisplay(event.event_type)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          className={`${OUTCOME_COLORS[outcomeKey] || "bg-slate-500/20 text-slate-300"} border`}
                          data-testid={`intake-event-outcome-${event.id}`}
                        >
                          <OutcomeIcon className="h-3 w-3 mr-1" />
                          {getOutcomeDisplay(outcomeKey)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-300 text-sm font-mono text-xs">
                        {event.lead_id ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="cursor-help">
                                  {truncateId(event.lead_id)}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="bg-slate-800 border-white/10">
                                <p className="font-mono text-xs">{event.lead_id}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          <span className="text-slate-500">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-300 text-sm font-mono text-xs">
                        {event.ghl_contact_id ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="cursor-help">
                                  {truncateId(event.ghl_contact_id)}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="bg-slate-800 border-white/10">
                                <p className="font-mono text-xs">{event.ghl_contact_id}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          <span className="text-slate-500">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-300 text-sm">
                        {event.event_type === "appointment_webhook" ? (
                          <div className="space-y-1">
                            {event.external_appointment_id && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="font-mono text-xs cursor-help">
                                      {truncateId(event.external_appointment_id)}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent className="bg-slate-800 border-white/10">
                                    <p className="font-mono text-xs">{event.external_appointment_id}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            {event.calendar_name && (
                              <div className="text-xs text-slate-400 truncate max-w-[150px]" title={event.calendar_name}>
                                {event.calendar_name}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-500">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewEventDetail(event)}
                          className="h-7 text-xs text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/10"
                          data-testid={`intake-event-view-${event.id}`}
                        >
                          <ExternalLink className="h-3 w-3 mr-1" />
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
        
        {!loading && events.length > 0 && (
          <div className="mt-2 text-xs text-slate-500 text-right">
            Showing {events.length} of {totalCount} events
          </div>
        )}
      </CardContent>

      {/* Event Detail Dialog */}
      <Dialog 
        open={detailDialog.open} 
        onOpenChange={(open) => !open && setDetailDialog({ open: false, event: null, loading: false })}
      >
        <DialogContent className="bg-slate-950 text-slate-100 ring-1 ring-white/10 max-w-2xl max-h-[80vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {detailDialog.event?.event_type === "appointment_webhook" ? (
                <Calendar className="h-5 w-5 text-purple-400" />
              ) : (
                <User className="h-5 w-5 text-indigo-400" />
              )}
              {detailDialog.event?.event_type === "appointment_webhook" 
                ? "Appointment Webhook Details" 
                : "Intake Event Details"}
            </DialogTitle>
          </DialogHeader>
          
          {detailDialog.loading ? (
            <div className="py-8 flex flex-col items-center gap-2">
              <Skeleton className="h-4 w-48 bg-white/10" />
              <Skeleton className="h-4 w-32 bg-white/10" />
            </div>
          ) : detailDialog.event ? (
            <div className="space-y-4 w-full overflow-hidden">
              {/* P1: Deep Link Buttons */}
              {(detailDialog.event.lead_id || detailDialog.event.appointment_id) && (
                <div className="flex flex-wrap gap-2 pb-2 border-b border-white/10">
                  {detailDialog.event.lead_id && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setDetailDialog({ open: false, event: null, loading: false });
                        navigate(`/leads/${detailDialog.event.lead_id}`);
                      }}
                      className="bg-indigo-500/10 border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/20 hover:text-indigo-200"
                      data-testid="intake-event-open-lead"
                    >
                      <User className="h-4 w-4 mr-2" />
                      Open Lead
                      <ArrowUpRight className="h-3 w-3 ml-1.5" />
                    </Button>
                  )}
                  {detailDialog.event.appointment_id && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setDetailDialog({ open: false, event: null, loading: false });
                        navigate(`/appointments/${detailDialog.event.appointment_id}`);
                      }}
                      className="bg-purple-500/10 border-purple-500/30 text-purple-300 hover:bg-purple-500/20 hover:text-purple-200"
                      data-testid="intake-event-open-appointment"
                    >
                      <Calendar className="h-4 w-4 mr-2" />
                      Open Appointment
                      <ArrowUpRight className="h-3 w-3 ml-1.5" />
                    </Button>
                  )}
                </div>
              )}
              
              {/* Status & Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-400">Event Type</Label>
                  <div className="mt-1">
                    <Badge className={`${EVENT_TYPE_COLORS[detailDialog.event.event_type]} border`}>
                      {getEventTypeDisplay(detailDialog.event.event_type)}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Outcome</Label>
                  <div className="mt-1">
                    <Badge className={`${OUTCOME_COLORS[detailDialog.event.outcome || detailDialog.event.status]} border`}>
                      {getOutcomeDisplay(detailDialog.event.outcome || detailDialog.event.status)}
                    </Badge>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Received At</Label>
                  <div className="mt-1 text-sm text-slate-200">
                    {formatDate(detailDialog.event.received_at)}
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Status Code</Label>
                  <div className="mt-1 text-sm text-slate-200">
                    {detailDialog.event.status_code || "—"}
                  </div>
                </div>
              </div>
              
              {/* IDs Section */}
              <div className="p-3 rounded-lg bg-white/5 border border-white/10 space-y-2 w-full overflow-hidden">
                <Label className="text-xs text-slate-400">Identifiers</Label>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {detailDialog.event.lead_id && (
                    <div className="min-w-0">
                      <span className="text-slate-400 text-xs">MissionAssist ID:</span>
                      <div className="font-mono text-xs text-slate-200 break-all truncate" title={detailDialog.event.lead_id}>
                        {detailDialog.event.lead_id}
                      </div>
                    </div>
                  )}
                  {detailDialog.event.ghl_contact_id && (
                    <div className="min-w-0">
                      <span className="text-slate-400 text-xs">GHL Contact ID:</span>
                      <div className="font-mono text-xs text-slate-200 break-all truncate" title={detailDialog.event.ghl_contact_id}>
                        {detailDialog.event.ghl_contact_id}
                      </div>
                    </div>
                  )}
                  {detailDialog.event.trace_id && (
                    <div className="min-w-0 col-span-2">
                      <span className="text-slate-400 text-xs">Trace ID:</span>
                      <div className="flex items-center gap-2 mt-0.5">
                        <code className="font-mono text-xs text-emerald-300 bg-emerald-500/10 px-2 py-1 rounded border border-emerald-500/20">
                          {detailDialog.event.trace_id}
                        </code>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  navigator.clipboard.writeText(detailDialog.event.trace_id);
                                  toast({
                                    title: "Trace ID copied",
                                    description: "You can now filter events by this trace ID",
                                  });
                                }}
                                className="h-6 px-2 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                                data-testid="intake-event-copy-trace-id"
                              >
                                <Copy className="h-3 w-3" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent className="bg-slate-800 border-white/10">
                              <p className="text-xs">Copy to clipboard</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setTraceIdFilter(detailDialog.event.trace_id);
                                  setDetailDialog({ open: false, event: null, loading: false });
                                  toast({
                                    title: "Filtering by trace ID",
                                    description: "Showing all events in this journey",
                                  });
                                }}
                                className="h-6 px-2 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                                data-testid="intake-event-filter-trace-id"
                              >
                                <ExternalLink className="h-3 w-3 mr-1" />
                                Filter
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent className="bg-slate-800 border-white/10">
                              <p className="text-xs">Show all events with this trace ID</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </div>
                  )}
                  {detailDialog.event.payload_hash && (
                    <div className="min-w-0">
                      <span className="text-slate-400 text-xs">Payload Hash:</span>
                      <div className="font-mono text-xs text-slate-200 truncate" title={detailDialog.event.payload_hash}>
                        {detailDialog.event.payload_hash}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Appointment-specific fields */}
              {detailDialog.event.event_type === "appointment_webhook" && (
                <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20 space-y-2">
                  <Label className="text-xs text-purple-400">Appointment Details</Label>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {detailDialog.event.external_appointment_id && (
                      <div>
                        <span className="text-slate-400 text-xs">External Appt ID:</span>
                        <div className="font-mono text-xs text-slate-200 break-all">
                          {detailDialog.event.external_appointment_id}
                        </div>
                      </div>
                    )}
                    {detailDialog.event.appointment_id && (
                      <div>
                        <span className="text-slate-400 text-xs">MA Appt ID:</span>
                        <div className="font-mono text-xs text-slate-200 break-all">
                          {detailDialog.event.appointment_id}
                        </div>
                      </div>
                    )}
                    {detailDialog.event.ghl_calendar_id && (
                      <div>
                        <span className="text-slate-400 text-xs">Calendar ID:</span>
                        <div className="font-mono text-xs text-slate-200 break-all">
                          {detailDialog.event.ghl_calendar_id}
                        </div>
                      </div>
                    )}
                    {detailDialog.event.calendar_name && (
                      <div className="col-span-2">
                        <span className="text-slate-400 text-xs">Calendar Name:</span>
                        <div className="text-sm text-slate-200">
                          {detailDialog.event.calendar_name}
                        </div>
                      </div>
                    )}
                    {detailDialog.event.start_at && (
                      <div>
                        <span className="text-slate-400 text-xs">Start Time:</span>
                        <div className="text-sm text-slate-200">
                          {formatDate(detailDialog.event.start_at)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Lead Source (for lead_intake) */}
              {detailDialog.event.event_type === "lead_intake" && detailDialog.event.lead_source_name && (
                <div className="p-3 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
                  <Label className="text-xs text-indigo-400">Lead Source</Label>
                  <div className="mt-1 text-sm text-slate-200">
                    {detailDialog.event.lead_source_name}
                  </div>
                </div>
              )}
              
              {/* MA-COMP-003: Consent Version Info (renamed from ma_consent_version to consent_version_id) */}
              {(detailDialog.event.consent_version_id || detailDialog.event.ma_consent_version || detailDialog.event.consent_version) && (
                <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                  <Label className="text-xs text-emerald-400">Consent Compliance</Label>
                  <div className="mt-2 space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-400 text-xs">Version ID:</span>
                      <code className="text-xs bg-slate-800 px-1.5 py-0.5 rounded text-emerald-300">
                        {detailDialog.event.consent_version_id || detailDialog.event.ma_consent_version || detailDialog.event.consent_version}
                      </code>
                    </div>
                    {detailDialog.event.consent_timestamp && (
                      <div className="flex items-center gap-2">
                        <span className="text-slate-400 text-xs">Consent Timestamp:</span>
                        <span className="text-sm text-slate-200">
                          {formatDate(detailDialog.event.consent_timestamp)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Error Message */}
              {detailDialog.event.error_message && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                  <Label className="text-xs text-red-400">Error Message</Label>
                  <div className="mt-1 text-sm text-red-200">
                    {detailDialog.event.error_message}
                  </div>
                </div>
              )}
              
              {/* Request Info */}
              <div className="p-3 rounded-lg bg-white/5 border border-white/10 space-y-2 w-full overflow-hidden">
                <Label className="text-xs text-slate-400">Request Info</Label>
                <div className="text-sm text-slate-300 space-y-1">
                  {detailDialog.event.remote_addr && (
                    <div className="min-w-0">
                      <span className="text-slate-400 text-xs">Remote Address:</span>{" "}
                      <span className="font-mono text-xs">{detailDialog.event.remote_addr}</span>
                    </div>
                  )}
                  {detailDialog.event.user_agent && (
                    <div className="min-w-0">
                      <span className="text-slate-400 text-xs">User Agent:</span>{" "}
                      <span className="text-xs block truncate" title={detailDialog.event.user_agent}>{detailDialog.event.user_agent}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Key Identifiers (from webhook payload) */}
              {detailDialog.event.key_identifiers && Object.keys(detailDialog.event.key_identifiers).length > 0 && (
                <div className="p-3 rounded-lg bg-white/5 border border-white/10 w-full overflow-hidden">
                  <Label className="text-xs text-slate-400">Key Identifiers (from payload)</Label>
                  <pre className="mt-2 text-xs text-slate-300 overflow-x-auto whitespace-pre-wrap break-all max-w-full">
                    {JSON.stringify(detailDialog.event.key_identifiers, null, 2)}
                  </pre>
                </div>
              )}
              
              {/* Raw Payload (PII redacted - staging only) */}
              {detailDialog.event.raw_payload_json && (
                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs text-amber-400 flex items-center gap-1">
                      <FileText className="h-3 w-3" />
                      Raw Webhook Payload (PII Redacted)
                    </Label>
                    <Badge className="bg-amber-500/20 text-amber-300 border border-amber-500/30 text-xs">
                      Staging Only
                    </Badge>
                  </div>
                  <pre className="mt-2 text-xs text-slate-300 overflow-x-auto whitespace-pre-wrap bg-slate-900/50 p-3 rounded max-h-96 overflow-y-auto">
                    {JSON.stringify(detailDialog.event.raw_payload_json, null, 2)}
                  </pre>
                </div>
              )}
              
              {/* Raw Payload Error (if capture failed) */}
              {detailDialog.event.raw_payload_error && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                  <Label className="text-xs text-red-400">Raw Payload Capture Error</Label>
                  <div className="mt-1 text-sm text-red-200">
                    {detailDialog.event.raw_payload_error}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default IntakeEventsViewer;
